import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Slice, Fragment, Node } from '@tiptap/pm/model'

/**
 * SmartClipboardExtension handles intelligent copy/paste behavior:
 * - Preserves rich formatting between TipTap editors
 * - Provides clean plain text for external applications
 * - Preserves line spacing when pasting from external sources
 */
const SmartClipboardExtension = Extension.create({
  name: 'smartClipboard',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('smartClipboard'),
        props: {
          handlePaste(view, event) {
            const clipboardData = event.clipboardData
            if (!clipboardData) return false

            const plainText = clipboardData.getData('text/plain')
            const htmlText = clipboardData.getData('text/html')

            // Detect if this is from an external source (like Notepad)
            // TipTap includes specific markers in its HTML
            const isFromTipTap = htmlText && (
              htmlText.includes('data-pm-slice') ||
              htmlText.includes('ProseMirror') ||
              htmlText.includes('data-type="paragraph"')
            )

            // If it's from external source and has plain text, handle it specially
            if (!isFromTipTap && plainText && plainText.trim().length > 0) {
              event.preventDefault()
              
              // Parse external text preserving line breaks
              const lines = plainText.split(/\r\n|\n|\r/)
              const nodes: Node[] = []

              lines.forEach((line) => {
                // Create paragraph for each line, including empty ones to preserve spacing
                const nodeJson: {
                  type: string
                  content?: { type: string; text: string }[]
                } = { type: 'paragraph' }
                if (line.length > 0) {
                  nodeJson.content = [{ type: 'text', text: line }]
                }
                const node = Node.fromJSON(view.state.schema, nodeJson)
                nodes.push(node)
              })

              const fragment = Fragment.fromArray(nodes)
              const slice = Slice.maxOpen(fragment)
              
              const { tr } = view.state
              tr.replaceSelection(slice)
              view.dispatch(tr)
              return true
            }

            // Let TipTap handle internal pastes with rich formatting
            return false
          },

          clipboardTextSerializer(slice) {
            // This customizes how TipTap serializes plain text for the clipboard
            // External apps will use this clean format without paragraph spacing
            return slice.content.textBetween(0, slice.content.size, '\n')
          },

          handleDOMEvents: {
            copy(view, event) {
              const { from, to } = view.state.selection
              if (from === to) return false // No selection

              // Get clean plain text only
              const selectedText = view.state.doc.textBetween(from, to, '\n')
              
              // Clear clipboard and set ONLY plain text
              event.clipboardData?.clearData()
              event.clipboardData?.setData('text/plain', selectedText)
              
              // Don't add any HTML or internal formats - pure plain text only
              event.preventDefault()
              return true
            },
          },
        },
      }),
    ]
  },
})

export default SmartClipboardExtension